import { CheckCircle, SwitchHorizontal01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import {
  RadioButton,
  RadioGroup,
} from '@/lib/base/radio-buttons/radio-buttons';
import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';

import { setOAuthInstanceRoutingCookie } from '../../oauth-instance-cookie';
import { getSalesforceInstallUrl } from '../api/install';
import { isValidMyDomainHost, toLoginHost } from '../hostValidation';
import { SalesforceIcon } from '../SalesforceIcon';
import type { LoginHostSelection } from '../types';

interface ConnectSalesforceButtonProps {
  orgId: number | string;
  disabled?: boolean;
}

const BENEFITS = [
  'Import your Salesforce accounts, contacts, and opportunities into Ahoy',
  'Keep key fields in sync as records change in Salesforce',
];

const BENEFIT_CLASS =
  'flex gap-2 text-body-small text-[var(--color-text-secondary)]';

const CONNECTOR_TILE_CLASS =
  'flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-xs';

const CARD_CLASS =
  'max-w-2xl rounded-xl border border-[var(--color-border-secondary)] p-6';

const CUSTOM_HOST_ERROR = 'Enter a My Domain like acme.my.salesforce.com.';

export function ConnectSalesforceButton({
  orgId,
  disabled,
}: ConnectSalesforceButtonProps) {
  const [selection, setSelection] = useState<LoginHostSelection>('production');
  const [customHost, setCustomHost] = useState('');
  const [touched, setTouched] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  // Two distinct failures, two distinct messages: `hostError` is the local
  // My-Domain validation (shown under the input), `connectError` is whatever the
  // server said (shown by the button). Folding them into one state made a
  // credentials-not-configured response render as "Enter a My Domain like…".
  const [hostError, setHostError] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const handleSelectionChange = useCallback((value: string) => {
    setSelection(value as LoginHostSelection);
    setHostError(false);
    setConnectError(null);
  }, []);

  const handleCustomHostChange = useCallback((value: string) => {
    setCustomHost(value);
    setHostError(false);
    setConnectError(null);
  }, []);

  const handleCustomHostBlur = useCallback(() => {
    setTouched(true);
  }, []);

  const handleConnect = useCallback(async () => {
    const loginHost = toLoginHost(selection, customHost);
    if (!loginHost) {
      setTouched(true);
      setHostError(true);
      return;
    }
    setIsConnecting(true);
    setHostError(false);
    setConnectError(null);
    // Set the routing cookie BEFORE the request, not after: the install call
    // itself writes the PKCE verifier into the session, so it has to land on the
    // same instance the oauth-gateway will route the callback to. (HubSpot sets
    // it after, because its install call stores no session state.)
    setOAuthInstanceRoutingCookie();
    try {
      const { url } = await getSalesforceInstallUrl(orgId, loginHost);
      window.location.href = url;
    } catch (e) {
      console.error(e);
      setConnectError(
        "We couldn't start the Salesforce connection. Try again."
      );
      setIsConnecting(false);
    }
  }, [selection, customHost, orgId]);

  const showCustomHostError =
    hostError ||
    (touched &&
      customHost.trim().length > 0 &&
      !isValidMyDomainHost(customHost));

  const isConnectDisabled =
    disabled ||
    isConnecting ||
    (selection === 'custom' && !isValidMyDomainHost(customHost));

  return (
    <div className={CARD_CLASS}>
      <div className='flex items-center gap-3'>
        <span className={CONNECTOR_TILE_CLASS}>
          <SalesforceIcon className='h-6 w-6 text-[var(--color-text-primary)]' />
        </span>
        <SwitchHorizontal01
          className='h-5 w-5 text-[var(--color-text-tertiary)]'
          aria-label='one-way import'
        />
        <span className={CONNECTOR_TILE_CLASS}>
          <AhoyLogoMinimal className='h-6 w-6' />
        </span>
      </div>

      <div className='mt-5 space-y-1'>
        <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
          Connect your Salesforce org
        </h3>
        <p className='text-body-small text-[var(--color-text-secondary)]'>
          Import accounts, contacts, and opportunities from Salesforce into
          Ahoy.
        </p>
      </div>

      <ul className='mt-4 space-y-2'>
        {BENEFITS.map(benefit => (
          <li key={benefit} className={BENEFIT_CLASS}>
            <CheckCircle
              className='mt-0.5 h-4 w-4 shrink-0 text-[var(--color-fg-success-primary)]'
              aria-hidden='true'
            />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      <div className='mt-5 space-y-2'>
        <span className='text-body-small font-medium text-[var(--color-text-primary)]'>
          Salesforce login
        </span>
        <RadioGroup
          value={selection}
          onChange={handleSelectionChange}
          aria-label='Salesforce login host'
        >
          <RadioButton value='production' label='Production' />
          <RadioButton value='sandbox' label='Sandbox' />
          <RadioButton value='custom' label='Custom My Domain' />
        </RadioGroup>

        {selection === 'custom' && (
          <Input
            type='text'
            value={customHost}
            onChange={handleCustomHostChange}
            onBlur={handleCustomHostBlur}
            placeholder='acme.my.salesforce.com'
            aria-label='Salesforce My Domain host'
            isInvalid={showCustomHostError}
          />
        )}
        {showCustomHostError && (
          <p className='text-body-small text-[var(--color-text-error-primary)]'>
            {CUSTOM_HOST_ERROR}
          </p>
        )}
      </div>

      <div className='mt-6 flex flex-wrap items-center gap-x-4 gap-y-2'>
        <Button
          color='primary'
          size='md'
          onClick={handleConnect}
          isDisabled={isConnectDisabled}
          isLoading={isConnecting}
          showTextWhileLoading
        >
          {isConnecting ? 'Connecting…' : 'Connect Salesforce'}
        </Button>
      </div>

      {connectError && (
        <p className='mt-3 text-sm text-utility-error-700'>{connectError}</p>
      )}
    </div>
  );
}
